<template>
  <div class="codemirror-wrapper" ref="editorContainer"></div>
</template>

<script>
import { EditorView, keymap, lineNumbers, highlightActiveLine, highlightActiveLineGutter } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { html } from '@codemirror/lang-html';
import { javascript } from '@codemirror/lang-javascript';
import { search, searchKeymap } from '@codemirror/search';
import { autocompletion, closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete';
import { indentOnInput, bracketMatching, foldGutter, foldKeymap } from '@codemirror/language';

// Vuetify component autocomplete
const vuetifyComponents = [
  // Layout
  { label: 'v-container', detail: 'Layout container', type: 'component' },
  { label: 'v-row', detail: 'Flex row', type: 'component' },
  { label: 'v-col', detail: 'Flex column', type: 'component' },
  { label: 'v-spacer', detail: 'Spacer', type: 'component' },
  { label: 'v-divider', detail: 'Divider line', type: 'component' },

  // Cards
  { label: 'v-card', detail: 'Material card', type: 'component' },
  { label: 'v-card-title', detail: 'Card title', type: 'component' },
  { label: 'v-card-subtitle', detail: 'Card subtitle', type: 'component' },
  { label: 'v-card-text', detail: 'Card content', type: 'component' },
  { label: 'v-card-actions', detail: 'Card actions', type: 'component' },

  // Buttons
  { label: 'v-btn', detail: 'Material button', type: 'component' },
  { label: 'v-btn-toggle', detail: 'Button toggle group', type: 'component' },
  { label: 'v-icon', detail: 'Material icon', type: 'component' },

  // Forms
  { label: 'v-text-field', detail: 'Text input', type: 'component' },
  { label: 'v-textarea', detail: 'Multi-line text input', type: 'component' },
  { label: 'v-select', detail: 'Select dropdown', type: 'component' },
  { label: 'v-autocomplete', detail: 'Autocomplete input', type: 'component' },
  { label: 'v-combobox', detail: 'Combobox input', type: 'component' },
  { label: 'v-checkbox', detail: 'Checkbox', type: 'component' },
  { label: 'v-radio', detail: 'Radio button', type: 'component' },
  { label: 'v-radio-group', detail: 'Radio button group', type: 'component' },
  { label: 'v-switch', detail: 'Switch toggle', type: 'component' },
  { label: 'v-slider', detail: 'Slider input', type: 'component' },
  { label: 'v-file-input', detail: 'File input', type: 'component' },
  { label: 'v-form', detail: 'Form container', type: 'component' },

  // Data tables
  { label: 'v-data-table', detail: 'Data table', type: 'component' },
  { label: 'v-simple-table', detail: 'Simple table', type: 'component' },

  // Lists
  { label: 'v-list', detail: 'List container', type: 'component' },
  { label: 'v-list-item', detail: 'List item', type: 'component' },
  { label: 'v-list-item-title', detail: 'List item title', type: 'component' },
  { label: 'v-list-item-subtitle', detail: 'List item subtitle', type: 'component' },
  { label: 'v-list-item-content', detail: 'List item content', type: 'component' },
  { label: 'v-list-item-action', detail: 'List item action', type: 'component' },
  { label: 'v-list-item-avatar', detail: 'List item avatar', type: 'component' },
  { label: 'v-list-item-group', detail: 'List item group', type: 'component' },

  // Navigation
  { label: 'v-app-bar', detail: 'Application bar', type: 'component' },
  { label: 'v-toolbar', detail: 'Toolbar', type: 'component' },
  { label: 'v-tabs', detail: 'Tabs', type: 'component' },
  { label: 'v-tab', detail: 'Tab', type: 'component' },
  { label: 'v-tabs-items', detail: 'Tab items container', type: 'component' },
  { label: 'v-tab-item', detail: 'Tab item', type: 'component' },
  { label: 'v-navigation-drawer', detail: 'Navigation drawer', type: 'component' },
  { label: 'v-bottom-navigation', detail: 'Bottom navigation', type: 'component' },
  { label: 'v-breadcrumbs', detail: 'Breadcrumbs', type: 'component' },

  // Dialogs & overlays
  { label: 'v-dialog', detail: 'Dialog overlay', type: 'component' },
  { label: 'v-menu', detail: 'Menu overlay', type: 'component' },
  { label: 'v-tooltip', detail: 'Tooltip', type: 'component' },
  { label: 'v-snackbar', detail: 'Snackbar notification', type: 'component' },
  { label: 'v-overlay', detail: 'Overlay', type: 'component' },

  // Progress & loaders
  { label: 'v-progress-circular', detail: 'Circular progress', type: 'component' },
  { label: 'v-progress-linear', detail: 'Linear progress', type: 'component' },
  { label: 'v-skeleton-loader', detail: 'Skeleton loader', type: 'component' },

  // Misc
  { label: 'v-alert', detail: 'Alert box', type: 'component' },
  { label: 'v-avatar', detail: 'Avatar', type: 'component' },
  { label: 'v-badge', detail: 'Badge', type: 'component' },
  { label: 'v-banner', detail: 'Banner', type: 'component' },
  { label: 'v-chip', detail: 'Chip', type: 'component' },
  { label: 'v-expansion-panels', detail: 'Expansion panels', type: 'component' },
  { label: 'v-expansion-panel', detail: 'Expansion panel', type: 'component' },
  { label: 'v-expansion-panel-header', detail: 'Expansion panel header', type: 'component' },
  { label: 'v-expansion-panel-content', detail: 'Expansion panel content', type: 'component' },
  { label: 'v-img', detail: 'Image', type: 'component' },
  { label: 'v-carousel', detail: 'Carousel', type: 'component' },
  { label: 'v-stepper', detail: 'Stepper', type: 'component' },
  { label: 'v-timeline', detail: 'Timeline', type: 'component' }
];

function vuetifyCompletion(context) {
  const word = context.matchBefore(/v-[\w-]*/);
  if (!word || (word.from === word.to && !context.explicit)) {
    return null;
  }

  return {
    from: word.from,
    options: vuetifyComponents
  };
}

export default {
  name: 'CodeMirrorEditor',
  props: {
    value: {
      type: String,
      default: ''
    },
    language: {
      type: String,
      default: 'html', // 'html' or 'javascript'
      validator: (val) => ['html', 'javascript'].includes(val)
    },
    readonly: {
      type: Boolean,
      default: false
    },
    height: {
      type: String,
      default: '400px'
    }
  },
  data() {
    return {
      editorView: null
    };
  },
  mounted() {
    this.initEditor();
  },
  beforeDestroy() {
    if (this.editorView) {
      this.editorView.destroy();
    }
  },
  watch: {
    value(newValue) {
      if (this.editorView) {
        const currentValue = this.editorView.state.doc.toString();
        if (currentValue !== newValue) {
          this.editorView.dispatch({
            changes: {
              from: 0,
              to: currentValue.length,
              insert: newValue
            }
          });
        }
      }
    },
    language() {
      // Recreate editor with new language
      if (this.editorView) {
        this.editorView.destroy();
        this.initEditor();
      }
    }
  },
  methods: {
    initEditor() {
      const languageExtension = this.language === 'html' ? html() : javascript();

      const updateListener = EditorView.updateListener.of((update) => {
        if (update.docChanged && !this.readonly) {
          const newValue = update.state.doc.toString();
          this.$emit('input', newValue);
        }
      });

      const theme = EditorView.theme({
        '&': {
          height: this.height,
          fontSize: '14px',
          border: '1px solid #e0e0e0',
          borderRadius: '4px'
        },
        '.cm-scroller': {
          overflow: 'auto',
          fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace"
        },
        '.cm-content': {
          padding: '10px 0'
        },
        '.cm-gutters': {
          backgroundColor: '#f5f5f5',
          color: '#999',
          border: 'none'
        },
        '.cm-activeLineGutter': {
          backgroundColor: '#e8f4ff'
        },
        '.cm-line': {
          padding: '0 4px',
          lineHeight: '1.6'
        }
      });

      // Configure autocompletion with Vuetify components for HTML
      const completionConfig = this.language === 'html'
        ? autocompletion({ override: [vuetifyCompletion] })
        : autocompletion();

      const startState = EditorState.create({
        doc: this.value || '',
        extensions: [
          lineNumbers(),
          highlightActiveLine(),
          highlightActiveLineGutter(),
          history(),
          foldGutter(),
          indentOnInput(),
          bracketMatching(),
          closeBrackets(),
          completionConfig,
          search({ top: true }),
          languageExtension,
          keymap.of([
            ...defaultKeymap,
            ...historyKeymap,
            ...foldKeymap,
            ...searchKeymap,
            ...closeBracketsKeymap
          ]),
          updateListener,
          theme,
          EditorView.editable.of(!this.readonly)
        ]
      });

      this.editorView = new EditorView({
        state: startState,
        parent: this.$refs.editorContainer
      });
    }
  }
};
</script>

<style scoped>
.codemirror-wrapper {
  width: 100%;
}
</style>
